import React, { useState, useId } from 'react';
import { ArrowDownIcon, BookOpenCheckIcon, CheckCircle2Icon, ChevronDownIcon, CircleDotIcon, CreditCardIcon, DownloadIcon, FacebookIcon, FileTextIcon, HeartHandshakeIcon, InstagramIcon, LightbulbIcon, LockKeyholeIcon, MailIcon, NotebookPenIcon, ScaleIcon, ShieldCheckIcon, StarIcon, TimerIcon, UsersIcon, YoutubeIcon, ZapIcon, BoxIcon } from 'lucide-react';
import { PurchaseButton } from '../components/PurchaseButton';
import { Reveal } from '../components/Reveal';
import { SectionHeading } from '../components/SectionHeading';
type Tome = {
  number: string;
  title: string;
  copy: string;
  icon: BoxIcon;
  color: string;
};
const tomes: Tome[] = [{
  number: 'TOMO 1',
  title: 'Cuando enseñar comienza a pesar',
  copy: 'Reconocer el agotamiento sin culpa, la carga invisible y las señales que merecen atención. El primer principio del método.',
  icon: HeartHandshakeIcon,
  color: 'bg-[#9a715d]'
}, {
  number: 'TOMO 2',
  title: 'Los pensamientos que aumentan el peso',
  copy: 'Identificar pensamientos automáticos, los cinco patrones más frecuentes en docencia y el perfeccionismo disfrazado de compromiso.',
  icon: LightbulbIcon,
  color: 'bg-[#b87858]'
}, {
  number: 'TOMO 3',
  title: 'Sustituir sin negar',
  copy: 'El Canal de Sustitución Consciente™: pensamientos puente creíbles, atención como dirección y reflexión aplicada con responsabilidad.',
  icon: ScaleIcon,
  color: 'bg-[#7d8c72]'
}, {
  number: 'TOMO 4',
  title: 'De la inacción al primer paso',
  copy: 'El Activador del Primer Paso™: la regla del mínimo posible, por qué la inacción no es pereza y cómo crear evidencia personal.',
  icon: ZapIcon,
  color: 'bg-[#a45c45]'
}, {
  number: 'TOMO 5',
  title: 'Recuperar el equilibrio',
  copy: 'Integración del Método CAMBIA™, rutina de cierre de jornada, semáforo de bienestar y plan de continuidad personal.',
  icon: CircleDotIcon,
  color: 'bg-[#596b58]'
}];
const painPoints: {
  icon: BoxIcon;
  title: string;
  copy: string;
}[] = [{
  icon: TimerIcon,
  title: 'El domingo que ya pesa',
  copy: 'Todavía es fin de semana y tu cabeza ya está en las tareas pendientes, los mensajes sin responder y la planificación que no terminó.'
}, {
  icon: LightbulbIcon,
  title: 'El pensamiento que no para',
  copy: '“No voy a poder”. “Nunca hago suficiente”. “Quizás no estoy hecha/o para esto”. Aparecen solos, sin que los invites.'
}, {
  icon: HeartHandshakeIcon,
  title: 'El cansancio que no se va',
  copy: 'Dormiste. Descansaste. Y aun así llegás al lunes sintiéndote en déficit antes de empezar.'
}, {
  icon: ZapIcon,
  title: 'El bloqueo de inicio',
  copy: 'Sabés lo que tenés que hacer. Pero algo dentro tuyo no arranca. Y después viene la culpa.'
}, {
  icon: ScaleIcon,
  title: 'La sensación de que nunca alcanza',
  copy: 'Hacés mucho. Pero al final del día solo ves lo que faltó. Nunca lo que estuvo bien.'
}];
const needIndex = [['Con el nudo en el estómago del domingo', 'Tomo 1: Reconocer el agotamiento'], ['Atrapada/o en pensamientos de “no llego”', 'Tomo 2: Identificar pensamientos automáticos'], ['Necesitando cambiar la interpretación', 'Tomo 3: Canal de Sustitución Consciente™'], ['Bloqueada/o sin poder empezar', 'Tomo 4: Activador del Primer Paso™'], ['Queriendo construir una rutina sostenible', 'Tomo 5: Recuperar el equilibrio'], ['Queriendo practicar 21 días seguidos', 'Diario CAMBIA™ incluido en el bono'], ['Después de una semana muy difícil', 'Tomo 1 + Diario CAMBIA™'], ['Con poco tiempo pero queriendo avanzar', 'Microacciones de cada tomo · 5 minutos']];
const testimonials = [{
  quote: 'Antes el domingo a la tarde era insoportable. Ahora escribo mis dos prioridades del lunes y puedo soltar el resto. El método funciona.',
  name: 'Maestra Laura M.',
  role: 'Educación Primaria — Montevideo'
}, {
  quote: 'Lo que más me sorprendió es que no me pide que finja estar bien. Me acompaña desde donde realmente estoy. Eso es lo que necesitaba.',
  name: 'Maestra Valentina R.',
  role: 'Jardín de Infantes — Colonia del Sacramento'
}, {
  quote: 'Pensé que era otro material de autoayuda genérico. No es así. Está pensado para la realidad docente, con ejemplos que reconocés como propios.',
  name: 'Maestra Gabriela S.',
  role: 'Educación Inicial — Montevideo'
}, {
  quote: 'El Activador del Primer Paso™ me cambió la manera de empezar las tareas que postergaba. Ahora tengo una herramienta concreta para cuando me bloqueo.',
  name: 'Maestra Romina P.',
  role: 'Primer Ciclo — Rivera'
}, {
  quote: 'El Diario CAMBIA™ lo uso cada mañana. En diez minutos ya tengo claridad sobre el día y el pensamiento que más me estaba pesando.',
  name: 'Maestra Cecilia T.',
  role: 'Educación Primaria — Salto'
}];
const faqs = [['¿Cómo se realiza el pago?', 'El pago se realiza de forma 100% segura a través de Mercado Pago. Podés pagar con tarjeta de crédito, tarjeta de débito o saldo disponible en tu cuenta.'], ['¿Cómo recibo el material después de la compra?', 'Inmediatamente después de confirmar el pago recibís un email con los enlaces de descarga de los 5 tomos y el Diario CAMBIA™. El acceso es instantáneo.'], ['¿En qué formato vienen los materiales?', 'Todos los materiales son PDF digitales. No requieren ninguna aplicación especial para abrirse.'], ['¿Desde qué dispositivos puedo verlos?', 'Desde cualquier dispositivo: celular, tablet o computadora. iOS, Android o Windows.'], ['¿Para qué nivel educativo está pensado?', 'Principalmente para docentes de Educación Inicial y Primaria, aunque sus herramientas son aplicables a cualquier nivel educativo.'], ['¿Necesito mucho tiempo para usarlo?', 'No. Cada herramienta está diseñada para aplicarse en cinco a diez minutos. El diario lleva menos de diez minutos por día.'], ['¿Tiene enfoque religioso o espiritual?', 'No. Integra principios de metafísica práctica como herramientas de reflexión y desarrollo personal, sin doctrina espiritual ni religiosa.'], ['¿Es un tratamiento para el burnout?', 'No. Es un material educativo y de desarrollo personal. No reemplaza la orientación de profesionales de la salud mental.'], ['¿Cómo funciona la garantía de 7 días?', 'Si dentro de los primeros 7 días sentís que el material no es lo que esperabas, te devolvemos el 100% de tu dinero sin preguntas.'], ['¿Dónde puedo ver más recursos de Aula de Éxitos?', 'En www.tufenixdigital.store encontrás todos nuestros materiales disponibles.']];
function PaymentNote({
  light = false
}: {
  light?: boolean;
}) {
  return <p className={`mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium leading-5 ${light ? 'text-[#fffaf1]/75' : 'text-[#78665d]'}`} data-id="element-9">
      <span data-id="element-10">💳 Pago seguro a través de Mercado Pago</span>
      <span data-id="element-11">📄 100% digital en PDF</span>
      <span data-id="element-12">⚡ Recibís todo por email al instante</span>
    </p>;
}
function CheckList({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-3" data-id="element-13">
      {items.map(item => <li key={item} className="flex gap-2.5 text-sm font-semibold leading-5" data-id="element-14">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#7d8c72]" aria-hidden="true" data-id="element-15" />
          {item}
        </li>)}
    </ul>;
}
function PaperTome({
  title,
  tone,
  className = '',
  journal = false
}: {
  title: string;
  tone: string;
  className?: string;
  journal?: boolean;
}) {
  return <div className={`absolute w-40 rounded-2xl border border-white/50 ${tone} p-4 shadow-xl shadow-[#4b2d24]/15 ${className}`} data-id="element-16">
      <div className="flex items-center justify-between text-[8px] font-black tracking-[0.15em] text-white/70" data-id="element-17">
        <span data-id="element-18">{journal ? 'DIARIO' : 'AULA EN EQUILIBRIO'}</span>
        <BookOpenCheckIcon size={12} data-id="element-19" />
      </div>
      <div className="mt-10 border-t border-white/30 pt-3 text-lg font-black leading-[.94] tracking-[-0.06em] text-white" data-id="element-20">
        {title}
      </div>
      <div className="mt-7 h-1.5 w-10 rounded-full bg-white/70" data-id="element-21" />
    </div>;
}
function ProductMockup() {
  return <figure className="mx-auto w-full max-w-md" data-id="element-22">
      <div className="overflow-hidden rounded-[2rem] border-4 border-[#f3d6a1]/70 bg-[#6d4937] p-1.5 shadow-2xl shadow-black/30" data-id="element-23">
        <img src="https://cdn.magicpatterns.com/uploads/pLSvByMyWQi4eDZJGoxHRs/heylloyd-image-1784042027704.png" alt="Mockup del Método CAMBIA de Aula en Equilibrio con sus cinco tomos digitales" width="1024" height="1024" fetchPriority="high" className="aspect-square w-full rounded-[1.55rem] object-cover" data-id="element-24" />
      </div>
      <figcaption className="mt-3 text-center text-xs font-semibold text-[#f3d6a1]" data-id="element-25">
        5 tomos digitales + Diario CAMBIA de 21 días
      </figcaption>
    </figure>;
}
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const faqId = useId();
  return <main className="w-full overflow-hidden bg-[#fffaf1] text-[#4b2d24]" data-id="element-26">
      <div className="sticky top-0 z-40 bg-[#e6c587] px-4 py-2 text-center text-[11px] font-extrabold text-[#4b2d24]" data-id="element-27">
        Descubrí todos nuestros recursos para docentes en{' '}
        <a href="https://www.tufenixdigital.store" className="underline underline-offset-2" data-id="element-28">
          www.tufenixdigital.store
        </a>
      </div>

      <section id="inicio" className="bg-[#4b2d24] px-5 pb-16 pt-8 text-[#fffaf1] sm:px-8 lg:pb-24" data-id="element-29">
        <nav className="mx-auto flex max-w-6xl items-center justify-between" aria-label="Navegación principal" data-id="element-30">
          <a href="#inicio" className="text-lg font-black tracking-tight sm:text-xl" data-id="element-31">
            Aula de <span className="text-[#f3d6a1]" data-id="element-32">Éxitos</span>
          </a>
          <a href="#comprar" className="text-xs font-bold text-[#fffaf1]/75 hover:text-[#f3d6a1]" data-id="element-33">
            Ver el método
          </a>
        </nav>
        <div className="mx-auto grid max-w-6xl items-center gap-12 pt-12 lg:grid-cols-[1.1fr_.9fr]" data-id="element-34">
          <Reveal data-id="element-35">
            <h1 className="mt-5 max-w-2xl text-4xl font-black leading-[.98] tracking-[-0.055em] sm:text-5xl lg:text-6xl" data-id="element-36">
              Cuando el domingo a la tarde el nudo en el estómago ya llegó antes
              que el lunes.
            </h1>
            <p className="mt-5 max-w-xl text-xl font-bold leading-7 text-[#f3d6a1]" data-id="element-37">
              No perdiste la vocación. Llevás demasiado tiempo intentando
              sostenerlo todo sin las herramientas para hacerlo.
            </p>
            <p className="mt-5 max-w-xl text-base leading-7 text-[#fffaf1]/75" data-id="element-38">
              <b data-id="element-39">AULA EN EQUILIBRIO™ · Método CAMBIA™</b>
              <br data-id="element-40" />
              Un recorrido práctico para reconocer la sobrecarga, transformar
              los pensamientos que te frenan y recuperar el movimiento mediante
              pequeñas acciones conscientes.
            </p>
          </Reveal>
          <Reveal delay={0.1} data-id="element-41">
            <ProductMockup data-id="element-42" />
            <div className="mt-7 text-center" data-id="element-43">
              <PurchaseButton data-id="element-44">QUIERO EMPEZAR HOY</PurchaseButton>
              <div className="mx-auto max-w-md text-center" data-id="element-45">
                <PaymentNote light data-id="element-46" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#f3ead9] px-5 py-20 sm:px-8" data-id="element-47">
        <Reveal data-id="element-48">
          <SectionHeading eyebrow="¿ALGUNA VEZ TE PASÓ ESTO?" title="Ser docente hoy significa cargar con mucho más de lo que se ve desde afuera." data-id="element-49" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-50">
          {painPoints.map(({
          icon: Icon,
          title,
          copy
        }, index) => <Reveal key={title} delay={index * 0.04} data-id="element-51">
              <article className="h-full rounded-2xl border border-[#dfc8ab] bg-[#fffaf1] p-6 shadow-sm" data-id="element-52">
                <Icon className="h-9 w-9 rounded-xl bg-[#f4ddd4] p-2 text-[#a45c45]" data-id="element-53" />
                <h3 className="mt-5 text-lg font-black" data-id="element-54">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-[#6e5a50]" data-id="element-55">{copy}</p>
              </article>
            </Reveal>)}
        </div>
      </section>

      <section className="bg-[#fffaf1] px-5 py-20 sm:px-8" data-id="element-56">
        <div className="mx-auto max-w-4xl" data-id="element-57">
          <Reveal data-id="element-58">
            <SectionHeading eyebrow="Y LO PEOR ES ESTO" title="Cada vez que pasa eso, terminás sintiéndote así:" data-id="element-59" />
          </Reveal>
          <Reveal delay={0.06} data-id="element-60">
            <ul className="mt-10 space-y-4 rounded-2xl bg-[#f7e5dc] p-6 sm:p-8" data-id="element-61">
              {['Buscás cómo sentirte mejor y solo encontrás consejos de “pensá positivo” que no te creés.', 'Intentás descansar pero la cabeza no para aunque el cuerpo esté quieto.', 'Llegás a casa agotada/o después de haber sostenido todo sola/o todo el día.', 'Sabés que algo tiene que cambiar pero no tenés una herramienta concreta para empezar.', 'Sentís que el cansancio que tenés ya no es cansancio normal. Pero tampoco sabés qué es.'].map(item => <li key={item} className="flex gap-3 text-sm font-semibold leading-6 text-[#613c32]" data-id="element-62">
                  <span className="font-black text-[#bf5e42]" aria-hidden="true" data-id="element-63">
                    ×
                  </span>
                  {item}
                </li>)}
            </ul>
            <p className="mt-9 text-center text-2xl font-black leading-tight" data-id="element-64">
              ¿Y si pudieras tener un método propio para transformar lo que te
              frena en una acción posible?
            </p>
            <div className="mt-7 text-center" data-id="element-65">
              <PurchaseButton data-id="element-66">QUIERO ESA HERRAMIENTA</PurchaseButton>
              <PaymentNote data-id="element-67" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#e9eee3] px-5 py-20 sm:px-8" data-id="element-68">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2" data-id="element-69">
          <Reveal className="order-2 lg:order-1" data-id="element-70">
            <p className="inline-flex rounded-full bg-[#d4dfd0] px-3 py-1 text-[10px] font-black tracking-wider text-[#526954]" data-id="element-71">
              LA SOLUCIÓN
            </p>
            <h2 className="mt-3 text-4xl font-black leading-[1.02] tracking-[-0.04em]" data-id="element-72">
              AULA EN EQUILIBRIO™
              <br data-id="element-73" />
              Método CAMBIA™
            </h2>
            <p className="mt-5 text-base leading-7 text-[#6e5a50]" data-id="element-74">
              5 tomos 100% digitales en PDF + Diario de 21 días para reconocer
              la sobrecarga docente, transformar los pensamientos que generan
              bloqueo y recuperar el movimiento mediante pequeñas acciones
              conscientes.
            </p>
            <p className="mt-4 text-base leading-7 text-[#6e5a50]" data-id="element-75">
              Un material que descargás en segundos, guardás en tu dispositivo y
              usás cuando lo necesitás. Sin esperar. Sin envíos. Sin
              complicaciones.
            </p>
            <h3 className="mt-7 text-xl font-black" data-id="element-76">¿Por qué funciona?</h3>
            <p className="mt-2 text-sm leading-6 text-[#6e5a50]" data-id="element-77">
              Porque no es un libro para leer de principio a fin. Es un método
              que se aplica en cinco minutos, en el medio de una semana docente
              real.
            </p>
          </Reveal>
          <Reveal delay={0.08} className="order-1 mx-auto lg:order-2" data-id="element-78">
            <div className="grid w-72 grid-cols-2 gap-3 rounded-3xl bg-[#fffaf1] p-4 shadow-xl shadow-[#4b2d24]/10" data-id="element-79">
              {['Captar', 'Aceptar', 'Modificar', 'Buscar', 'Iniciar', 'Anclar'].map((word, index) => <div key={word} className={`flex aspect-square items-end rounded-2xl p-4 text-lg font-black ${index % 3 === 0 ? 'bg-[#bf5e42] text-[#fffaf1]' : index % 3 === 1 ? 'bg-[#e6c587] text-[#4b2d24]' : 'bg-[#7d8c72] text-white'}`} data-id="element-80">
                  {word}
                </div>)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fffaf1] px-5 py-20 sm:px-8" data-id="element-81">
        <Reveal data-id="element-82">
          <SectionHeading eyebrow="LO QUE RECIBÍS" title="5 tomos en PDF. Diario de 21 días. Una herramienta para cada momento." data-id="element-83" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-84">
          {tomes.map((tome, index) => {
          const Icon = tome.icon;
          return <Reveal key={tome.number} delay={index * 0.04} data-id="element-85">
                <article className="h-full overflow-hidden rounded-2xl border border-[#dfc8ab] bg-white shadow-sm" data-id="element-86">
                  <div className={`${tome.color} p-5 text-white`} data-id="element-87">
                    <p className="text-[10px] font-black tracking-widest" data-id="element-88">
                      {tome.number}
                    </p>
                    <Icon className="mt-4 h-9 w-9" data-id="element-89" />
                    <h3 className="mt-5 text-xl font-black leading-tight" data-id="element-90">
                      {tome.title}
                    </h3>
                  </div>
                  <div className="p-5" data-id="element-91">
                    <p className="text-sm leading-6 text-[#6e5a50]" data-id="element-92">
                      {tome.copy}
                    </p>
                    <p className="mt-5 flex items-center gap-1.5 border-t border-[#ead9c1] pt-4 text-xs font-bold text-[#7d8c72]" data-id="element-93">
                      <FileTextIcon size={14} data-id="element-94" /> PDF · 20 páginas
                    </p>
                  </div>
                </article>
              </Reveal>;
        })}
        </div>
        <Reveal className="mx-auto mt-8 max-w-3xl" data-id="element-95">
          <article className="rounded-2xl border border-[#d6bea0] bg-[#fbf0dc] p-6 sm:flex sm:items-center sm:gap-6" data-id="element-96">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-[#bf5e42] text-[#fffaf1]" data-id="element-97">
              <NotebookPenIcon size={27} data-id="element-98" />
            </div>
            <div data-id="element-99">
              <p className="text-xs font-black tracking-widest text-[#a45c45]" data-id="element-100">
                BONO ESPECIAL · INCLUIDO SIN COSTO EXTRA
              </p>
              <h3 className="mt-1 text-xl font-black" data-id="element-101">
                Diario CAMBIA™ · 21 días
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#6e5a50]" data-id="element-102">
                21 registros diarios guiados con los seis pasos del método, 3
                revisiones semanales, banco personal de pensamientos puente,
                frases de anclaje y plan de continuidad.
              </p>
              <p className="mt-3 text-xs font-bold text-[#7d8c72]" data-id="element-103">
                PDF · 20 páginas · Acceso inmediato · Incluido sin costo extra
              </p>
            </div>
          </article>
          <div className="mt-7 text-center" data-id="element-104">
            <PurchaseButton data-id="element-105">QUIERO MIS 5 TOMOS + DIARIO AHORA</PurchaseButton>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#4b2d24] px-5 py-20 text-[#fffaf1] sm:px-8" data-id="element-106">
        <Reveal data-id="element-107">
          <SectionHeading inverted eyebrow="¿CÓMO RECIBO MI MATERIAL?" title="Tres pasos simples. Sin complicaciones." data-id="element-108" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-3" data-id="element-109">
          {[[CreditCardIcon, 'PASO 1', 'Hacés tu pago', 'Completás la compra de forma segura a través de Mercado Pago desde tu celular o computadora.'], [MailIcon, 'PASO 2', 'Recibís tu email', 'Inmediatamente después del pago recibís los enlaces de descarga de los 5 tomos y el diario.'], [DownloadIcon, 'PASO 3', 'Descargás tus PDFs', 'Los descargás a tu dispositivo y los tenés disponibles para siempre, sin necesitar internet.']].map(([Icon, step, title, copy], index) => {
          const StepIcon = Icon as BoxIcon;
          return <Reveal key={String(step)} delay={index * 0.05} data-id="element-110">
                <article className="h-full rounded-2xl bg-[#644239] p-6" data-id="element-111">
                  <p className="text-[10px] font-black tracking-widest text-[#f3d6a1]" data-id="element-112">
                    {String(step)}
                  </p>
                  <StepIcon className="mt-4 h-10 w-10 text-[#d8e1d2]" data-id="element-113" />
                  <h3 className="mt-5 text-xl font-black" data-id="element-114">{String(title)}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#fffaf1]/75" data-id="element-115">
                    {String(copy)}
                  </p>
                </article>
              </Reveal>;
        })}
        </div>
        <p className="mx-auto mt-9 max-w-3xl text-center text-sm font-bold text-[#fffaf1]/85" data-id="element-116">
          Sin esperas. Sin envíos físicos. Sin complicaciones. Pagás y en
          minutos ya tenés todo en tu dispositivo.
        </p>
      </section>

      <section className="bg-[#f3ead9] px-5 py-20 sm:px-8" data-id="element-117">
        <Reveal data-id="element-118">
          <SectionHeading eyebrow="¿CÓMO SE PAGA?" title="Pago seguro a través de Mercado Pago." data-id="element-119" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-5xl gap-4 md:grid-cols-3" data-id="element-120">
          {[[CreditCardIcon, 'Tarjeta de crédito', 'Todas las tarjetas aceptadas'], [CheckCircle2Icon, 'Tarjeta de débito', 'Débito inmediato y seguro'], [LockKeyholeIcon, 'Saldo de Mercado Pago', 'Usá tu saldo disponible']].map(([Icon, title, copy]) => {
          const PaymentIcon = Icon as BoxIcon;
          return <article key={String(title)} className="rounded-2xl bg-[#fffaf1] p-6 text-center shadow-sm" data-id="element-121">
                <PaymentIcon className="mx-auto h-8 w-8 text-[#7d8c72]" data-id="element-122" />
                <h3 className="mt-3 font-black" data-id="element-123">{String(title)}</h3>
                <p className="mt-2 text-sm text-[#6e5a50]" data-id="element-124">{String(copy)}</p>
              </article>;
        })}
        </div>
        <div className="mx-auto mt-7 grid max-w-5xl gap-4 md:grid-cols-3" data-id="element-125">
          {[[LockKeyholeIcon, 'Pago 100% seguro', 'Cada transacción está protegida con tecnología de encriptación avanzada.'], [ZapIcon, 'Procesamiento inmediato', 'Tu pago se procesa en segundos. Sin esperas ni demoras.'], [MailIcon, 'Entrega automática por email', 'Al confirmar el pago recibís tus PDFs directamente en tu correo.']].map(([Icon, title, copy]) => {
          const PaymentIcon = Icon as BoxIcon;
          return <article key={String(title)} className="rounded-2xl border border-[#dfc8ab] bg-[#fffaf1] p-6" data-id="element-126">
                <PaymentIcon className="h-8 w-8 text-[#a45c45]" data-id="element-127" />
                <h3 className="mt-3 font-black" data-id="element-128">{String(title)}</h3>
                <p className="mt-2 text-sm leading-6 text-[#6e5a50]" data-id="element-129">
                  {String(copy)}
                </p>
              </article>;
        })}
        </div>
      </section>

      <section className="bg-[#fffaf1] px-5 py-20 sm:px-8" data-id="element-130">
        <Reveal data-id="element-131">
          <SectionHeading eyebrow="EL DIFERENCIAL" title="¿Por qué este método es diferente a todo lo que conocés?" data-id="element-132" />
        </Reveal>
        <Reveal delay={0.05} className="mx-auto mt-10 max-w-5xl overflow-hidden rounded-2xl border border-[#dfc8ab]" data-id="element-133">
          <div className="grid grid-cols-2 bg-[#4b2d24] text-[11px] font-black tracking-wide text-white sm:text-sm" data-id="element-134">
            <div className="p-4 text-white/65" data-id="element-135">LO QUE EXISTE HOY</div>
            <div className="bg-[#7d8c72] p-4" data-id="element-136">AULA EN EQUILIBRIO™</div>
          </div>
          {[['Piden que “pienses positivo” sin explicar cómo', 'Enseña a construir pensamientos puente creíbles y aplicables'], ['Son genéricos y no hablan de la realidad docente', 'Diseñado específicamente para maestras y maestros uruguayos'], ['No tienen método propio ni estructura clara', 'El Método CAMBIA™ es un sistema integrado de seis pasos'], ['Generan culpa cuando no podés “pensar positivo”', 'Reconoce el agotamiento sin juzgar ni exigir positividad forzada'], ['Requieren mucho tiempo para aplicarse', 'Cada herramienta funciona en cinco a diez minutos'], ['No conectan el pensamiento con la acción', 'Cada pensamiento puente termina en una acción concreta y posible'], ['Hay que esperar un envío físico', '100% digital, descargás al instante']].map(([oldWay, newWay]) => <div className="grid grid-cols-2 border-t border-[#ead9c1] text-xs sm:text-sm" key={oldWay} data-id="element-137">
              <div className="p-4 text-[#78665d]" data-id="element-138">
                <span className="mr-1 text-[#bf5e42]" data-id="element-139">×</span>
                {oldWay}
              </div>
              <div className="bg-[#eef1e8] p-4 font-bold text-[#526954]" data-id="element-140">
                <span className="mr-1" data-id="element-141">✓</span>
                {newWay}
              </div>
            </div>)}
        </Reveal>
      </section>

      <section className="bg-[#e9eee3] px-5 py-20 sm:px-8" data-id="element-142">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2" data-id="element-143">
          <Reveal data-id="element-144">
            <article className="rounded-2xl border-4 border-[#4b2d24] bg-[#fffaf1] p-5 shadow-xl" data-id="element-145">
              <p className="text-[10px] font-black tracking-widest text-[#7d8c72]" data-id="element-146">
                EJEMPLO DE APLICACIÓN DEL MÉTODO
              </p>
              <h3 className="mt-3 text-3xl font-black" data-id="element-147">
                El domingo a la tarde
              </h3>
              <dl className="mt-5 space-y-2 text-sm leading-6" data-id="element-148">
                {[['Situación', 'Son las 7 PM del domingo. La semana que empieza ya pesa.'], ['Pensamiento automático', '“No voy a poder. Tengo demasiado y ya estoy agotada/o.”'], ['Efecto', 'Ansiedad. Culpa. Dificultad para disfrutar el tiempo libre.'], ['Pensamiento puente', '“Tengo cosas pendientes. Puedo escribir las dos prioridades del lunes y soltar el resto hasta mañana.”'], ['Acción mínima', 'Escribir dos prioridades del lunes. Diez minutos. Cerrar el tema.'], ['Evidencia', 'Hay un plan mínimo. La ansiedad baja. El domingo puede terminar con algo de descanso real.']].map(([term, description]) => <div key={term} className="rounded-lg bg-[#f6eddf] p-3" data-id="element-149">
                    <dt className="font-black text-[#a45c45]" data-id="element-150">{term}</dt>
                    <dd className="mt-0.5 text-[#6e5a50]" data-id="element-151">{description}</dd>
                  </div>)}
              </dl>
            </article>
          </Reveal>
          <Reveal delay={0.08} data-id="element-152">
            <p className="inline-flex rounded-full bg-[#d4dfd0] px-3 py-1 text-[10px] font-black tracking-wider text-[#526954]" data-id="element-153">
              ASÍ SE APLICA EL MÉTODO
            </p>
            <h2 className="mt-3 text-3xl font-black leading-tight tracking-[-0.04em]" data-id="element-154">
              Sin positividad forzada. Sin negar la dificultad.
            </h2>
            <p className="mt-4 text-sm leading-6 text-[#6e5a50]" data-id="element-155">
              Con una interpretación más útil y una acción posible para la vida
              docente real.
            </p>
            <div className="mt-6" data-id="element-156">
              <CheckList items={['Fichas listas en PDF de alta calidad.', 'Ejercicios prácticos con tablas y cuadros de texto.', 'Herramientas aplicables en cinco minutos.', 'Preguntas de reflexión para cada situación.', 'Sección “Del pensamiento a la acción” en cada tomo.', 'Microacción y frase de cierre en cada tomo.']} data-id="element-157" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fffaf1] px-5 py-20 sm:px-8" data-id="element-158">
        <Reveal data-id="element-159">
          <SectionHeading eyebrow="ÍNDICE POR NECESIDAD" title="Encontrás la herramienta que necesitás en segundos." data-id="element-160" />
        </Reveal>
        <Reveal delay={0.05} className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl border border-[#dfc8ab]" data-id="element-161">
          <div className="grid grid-cols-[1.3fr_1fr] bg-[#4b2d24] p-4 text-xs font-black text-[#fffaf1]" data-id="element-162">
            <span data-id="element-163">SI ESTÁS…</span>
            <span data-id="element-164">IR AL</span>
          </div>
          {needIndex.map(([need, tome]) => <div className="grid grid-cols-[1.3fr_1fr] border-t border-[#ead9c1] text-xs sm:text-sm" key={need} data-id="element-165">
              <span className="p-4" data-id="element-166">{need}</span>
              <span className="bg-[#f6eddf] p-4 font-bold text-[#526954]" data-id="element-167">
                {tome}
              </span>
            </div>)}
        </Reveal>
      </section>

      <section id="comprar" className="bg-[#4b2d24] px-5 py-20 text-[#fffaf1] sm:px-8" data-id="element-168">
        <Reveal data-id="element-169">
          <SectionHeading inverted eyebrow="UNA SOLA INVERSIÓN" title="Todo el método por solo $490 pesos uruguayos." data-id="element-170" />
        </Reveal>
        <Reveal delay={0.06} className="mx-auto mt-10 max-w-4xl rounded-3xl bg-[#fffaf1] p-6 text-[#4b2d24] shadow-2xl sm:p-8" data-id="element-171">
          <div className="grid grid-cols-[1.4fr_1fr] border-b border-[#ead9c1] pb-3 text-xs font-black text-[#7d8c72]" data-id="element-172">
            <span data-id="element-173">QUÉ RECIBÍS</span>
            <span data-id="element-174">FORMATO</span>
          </div>
          {['Tomo 1: Cuando enseñar comienza a pesar', 'Tomo 2: Los pensamientos que aumentan el peso', 'Tomo 3: Sustituir sin negar', 'Tomo 4: De la inacción al primer paso', 'Tomo 5: Recuperar el equilibrio', 'BONO: Diario CAMBIA™ — 21 días de práctica guiada', 'Canal de Sustitución Consciente™', 'Activador del Primer Paso™', 'Semáforo de bienestar semanal', 'Banco personal de pensamientos puente', 'Rutina de cierre de jornada', 'Plan de continuidad personal', 'Acceso inmediato y de por vida', 'Garantía incondicional de 7 días'].map((item, index) => <div className="grid grid-cols-[1.4fr_1fr] border-b border-[#f6eddf] py-3 text-xs sm:text-sm" key={item} data-id="element-175">
              <span className="font-semibold" data-id="element-176">{item}</span>
              <span className="text-[#7d8c72]" data-id="element-177">
                {index < 12 ? 'PDF · Incluido' : index === 12 ? 'Al instante' : 'Sin preguntas'}
              </span>
            </div>)}
          <div className="pt-8 text-center" data-id="element-178">
            <p className="text-xs font-black tracking-widest text-[#7d8c72]" data-id="element-179">
              TODO ESTO POR SOLO
            </p>
            <p className="mt-2 text-5xl font-black text-[#bf5e42]" data-id="element-180">$490</p>
            <p className="text-lg font-bold" data-id="element-181">pesos uruguayos</p>
            <p className="mt-2 text-sm text-[#6e5a50]" data-id="element-182">
              100% PDF · Acceso inmediato · Pago seguro mediante Mercado Pago.
            </p>
            <div className="mt-6" data-id="element-183">
              <PurchaseButton data-id="element-184">
                QUIERO MI MÉTODO COMPLETO POR $490
              </PurchaseButton>
            </div>
            <PaymentNote data-id="element-185" />
          </div>
        </Reveal>
      </section>

      <section className="bg-[#f3ead9] px-5 py-20 sm:px-8" data-id="element-186">
        <Reveal data-id="element-187">
          <SectionHeading eyebrow="MÁS DE 500 DOCENTES YA LO USAN" title="Esto es lo que dicen quienes ya tienen el método." data-id="element-188" />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-6xl gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-189">
          {testimonials.map((testimonial, index) => <Reveal delay={index * 0.04} key={testimonial.name} data-id="element-190">
              <article className="h-full rounded-2xl border border-[#dfc8ab] bg-[#fffaf1] p-5 shadow-sm" data-id="element-191">
                <div className="flex gap-1 text-[#c48d52]" data-id="element-192">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-193" />)}
                </div>
                <blockquote className="mt-4 text-sm leading-6 text-[#6e5a50]" data-id="element-194">
                  “{testimonial.quote}”
                </blockquote>
                <div className="mt-5 border-t border-[#ead9c1] pt-3" data-id="element-195">
                  <p className="text-sm font-black" data-id="element-196">{testimonial.name}</p>
                  <p className="text-xs text-[#78665d]" data-id="element-197">{testimonial.role}</p>
                </div>
              </article>
            </Reveal>)}
        </div>
      </section>

      <section className="bg-[#fbf0dc] px-5 py-16 sm:px-8" data-id="element-198">
        <Reveal data-id="element-199">
          <div className="mx-auto max-w-5xl rounded-2xl border border-[#d6bea0] bg-[#fffaf1] p-7 text-center shadow-sm" data-id="element-200">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-[#bf5e42]" data-id="element-201" />
            <p className="mt-4 text-xs font-black tracking-widest text-[#a45c45]" data-id="element-202">
              CERO RIESGO PARA VOS
            </p>
            <h2 className="mt-2 text-3xl font-black" data-id="element-203">
              Probalo durante 7 días. Sin preguntas.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-6 text-[#6e5a50]" data-id="element-204">
              Si dentro de los primeros 7 días después de recibir tus PDFs
              sentís que el material no es lo que esperabas, te devolvemos el
              100% de tu dinero. Sin formularios complicados. Sin demoras.
            </p>
            <div className="mt-8 grid gap-5 sm:grid-cols-3" data-id="element-205">
              {[['7 días completos', 'Tenés una semana entera para recorrer el material.'], ['100% de tu dinero', 'Si no te convence te devolvemos todo lo que pagaste.'], ['Sin preguntas', 'No tenés que explicar ni justificar nada.']].map(([title, copy]) => <div key={title} data-id="element-206">
                  <CheckCircle2Icon className="mx-auto h-7 w-7 text-[#7d8c72]" data-id="element-207" />
                  <h3 className="mt-2 font-black" data-id="element-208">{title}</h3>
                  <p className="mt-1 text-sm text-[#6e5a50]" data-id="element-209">{copy}</p>
                </div>)}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#fffaf1] px-5 py-20 sm:px-8" data-id="element-210">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-[.7fr_1.3fr]" data-id="element-211">
          <Reveal data-id="element-212">
            <div className="mx-auto grid h-44 w-44 place-items-center rounded-full bg-[#e9eee3]" data-id="element-213">
              <UsersIcon className="h-16 w-16 text-[#7d8c72]" aria-hidden="true" data-id="element-214" />
            </div>
          </Reveal>
          <Reveal delay={0.08} data-id="element-215">
            <p className="inline-flex rounded-full bg-[#e4eadc] px-3 py-1 text-[10px] font-black tracking-wider text-[#526954]" data-id="element-216">
              ¿QUIÉN ESTÁ DETRÁS DE ESTO?
            </p>
            <h2 className="mt-3 text-3xl font-black" data-id="element-217">Somos Aula de Éxitos.</h2>
            <p className="mt-4 text-sm leading-6 text-[#6e5a50]" data-id="element-218">
              Creamos recursos educativos digitales para maestras y maestros que
              quieren hacer su trabajo con más herramientas, menos improvisación
              y más confianza.
            </p>
            <p className="mt-3 text-sm leading-6 text-[#6e5a50]" data-id="element-219">
              AULA EN EQUILIBRIO™ nació de escuchar, una y otra vez, la misma
              conversación silenciosa de docentes que cargan con demasiado sin
              las herramientas para gestionarlo.
            </p>
            <p className="mt-3 text-sm font-semibold text-[#526954]" data-id="element-220">
              No es positividad forzada. No es autoayuda genérica. Es un método
              propio para la realidad docente uruguaya.
            </p>
            <p className="mt-5 text-sm" data-id="element-221">
              Encontrá todos nuestros materiales en{' '}
              <a href="https://www.tufenixdigital.store" className="font-bold text-[#a45c45] underline" data-id="element-222">
                www.tufenixdigital.store
              </a>
            </p>
            <div className="mt-6 grid grid-cols-3 rounded-xl bg-[#f6eddf] p-4 text-center text-[#526954]" data-id="element-223">
              {[['5', 'tomos PDF'], ['500+', 'docentes'], ['4.9', 'valoración']].map(([value, label]) => <div key={label} data-id="element-224">
                  <b className="block text-lg" data-id="element-225">{value}</b>
                  <span className="text-[10px]" data-id="element-226">{label}</span>
                </div>)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#f3ead9] px-5 py-20 sm:px-8" data-id="element-227">
        <Reveal data-id="element-228">
          <SectionHeading eyebrow="PREGUNTAS FRECUENTES" title="Todo lo que necesitás saber antes de comprarlo." data-id="element-229" />
        </Reveal>
        <div className="mx-auto mt-9 max-w-4xl space-y-2" data-id="element-230">
          {faqs.map(([question, answer], index) => {
          const isOpen = openFaq === index;
          const panelId = `${faqId}-${index}`;
          return <article key={question} className="overflow-hidden rounded-xl border border-[#dfc8ab] bg-[#fffaf1]" data-id="element-231">
                <h3 data-id="element-232">
                  <button type="button" className="flex w-full items-center justify-between gap-4 p-5 text-left text-sm font-black text-[#4b2d24] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#bf5e42]" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenFaq(isOpen ? null : index)} data-id="element-233">
                    {question}
                    <ChevronDownIcon className={`h-5 w-5 shrink-0 text-[#a45c45] transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-234" />
                  </button>
                </h3>
                {isOpen ? <div id={panelId} role="region" className="border-t border-[#ead9c1] px-5 py-4 text-sm leading-6 text-[#6e5a50]" data-id="element-235">
                    {answer}
                  </div> : null}
              </article>;
        })}
        </div>
      </section>

      <section className="bg-[#7d8c72] px-5 py-20 text-center text-[#fffaf1] sm:px-8" data-id="element-236">
        <Reveal data-id="element-237">
          <div className="mx-auto max-w-3xl" data-id="element-238">
            <p className="text-xs font-black tracking-widest text-[#f3d6a1]" data-id="element-239">
              5 TOMOS EN PDF + DIARIO DE 21 DÍAS + BONO ESPECIAL
            </p>
            <h2 className="mt-4 text-3xl font-black leading-tight tracking-[-0.04em] sm:text-4xl" data-id="element-240">
              ¿Lista/o para tener un método propio cuando el agotamiento
              aparezca?
            </h2>
            <p className="mt-4 text-sm leading-6 text-[#fffaf1]/85" data-id="element-241">
              La próxima vez que el domingo pese, que los pensamientos no paren
              o que el bloqueo no te deje empezar, vas a tener todo lo que
              necesitás. Más de 500 docentes ya transformaron su manera de
              relacionarse con el trabajo con este método.
            </p>
            <p className="mt-7 text-4xl font-black text-[#f3d6a1]" data-id="element-242">
              $490 <span className="text-base text-white" data-id="element-243">pesos uruguayos</span>
            </p>
            <p className="mt-2 text-sm font-bold text-[#fffaf1]/90" data-id="element-244">
              Pago único · Sin cuotas · 100% digital · Acceso inmediato
            </p>
            <div className="mt-7" data-id="element-245">
              <PurchaseButton data-id="element-246">
                ASEGURAR MI MÉTODO COMPLETO POR $490
              </PurchaseButton>
            </div>
            <PaymentNote light data-id="element-247" />
          </div>
        </Reveal>
      </section>

      <section className="bg-[#4b2d24] px-5 py-12 text-center text-[#fffaf1] sm:px-8" data-id="element-248">
        <blockquote className="mx-auto max-w-3xl text-lg font-bold leading-7" data-id="element-249">
          “No se trata de negar el cansancio ni de obligarte a pensar en
          positivo. Se trata de reconocer lo que estás viviendo, transformar los
          pensamientos que te frenan y dar el primer paso posible.”
        </blockquote>
        <p className="mt-5 text-sm font-black" data-id="element-250">Aula de Éxitos</p>
        <a href="https://www.tufenixdigital.store" className="mt-1 inline-block text-sm text-[#f3d6a1]" data-id="element-251">
          www.tufenixdigital.store
        </a>
      </section>

      <a href="#comprar" className="fixed inset-x-3 bottom-3 z-30 flex min-h-14 items-center justify-center gap-2 rounded-full bg-[#bf5e42] px-5 text-center text-sm font-black text-[#fffaf1] shadow-2xl shadow-[#4b2d24]/35 focus:outline-none focus:ring-4 focus:ring-[#e9bba9] sm:hidden" data-id="element-252">
        QUIERO EL MÉTODO POR $490 <ArrowDownIcon size={18} aria-hidden="true" data-id="element-253" />
      </a>
      <footer className="bg-[#39211b] px-5 py-8 pb-24 text-center text-xs leading-6 text-[#fffaf1]/65 sm:pb-8" data-id="element-254">
        <p data-id="element-255">© 2025 Aula de Éxitos — Todos los derechos reservados.</p>
        <nav className="mt-5" aria-label="Redes sociales de Tu Fénix Digital" data-id="element-256">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-[#f3d6a1]" data-id="element-257">
            Seguinos en redes
          </p>
          <div className="mt-3 flex justify-center gap-3" data-id="element-258">
            <a href="https://www.facebook.com/people/Tu-Fenix-Digital/61590759474336/" target="_blank" rel="noreferrer" aria-label="Seguir a Tu Fénix Digital en Facebook" className="grid h-10 w-10 place-items-center rounded-full border border-[#fffaf1]/20 text-[#fffaf1] transition-colors hover:border-[#f3d6a1] hover:bg-[#f3d6a1] hover:text-[#4b2d24] focus:outline-none focus:ring-2 focus:ring-[#f3d6a1]" data-id="element-259">
              <FacebookIcon size={18} aria-hidden="true" data-id="element-260" />
            </a>
            <a href="https://www.instagram.com/tufenixdigital_oficial/" target="_blank" rel="noreferrer" aria-label="Seguir a Tu Fénix Digital en Instagram" className="grid h-10 w-10 place-items-center rounded-full border border-[#fffaf1]/20 text-[#fffaf1] transition-colors hover:border-[#f3d6a1] hover:bg-[#f3d6a1] hover:text-[#4b2d24] focus:outline-none focus:ring-2 focus:ring-[#f3d6a1]" data-id="element-261">
              <InstagramIcon size={18} aria-hidden="true" data-id="element-262" />
            </a>
            <a href="https://www.youtube.com/@TuFenixDigital" target="_blank" rel="noreferrer" aria-label="Visitar el canal de Tu Fénix Digital en YouTube" className="grid h-10 w-10 place-items-center rounded-full border border-[#fffaf1]/20 text-[#fffaf1] transition-colors hover:border-[#f3d6a1] hover:bg-[#f3d6a1] hover:text-[#4b2d24] focus:outline-none focus:ring-2 focus:ring-[#f3d6a1]" data-id="element-263">
              <YoutubeIcon size={18} aria-hidden="true" data-id="element-264" />
            </a>
          </div>
        </nav>
        <p className="mt-5" data-id="element-265">
          Pago seguro procesado a través de Mercado Pago · Transacciones 100%
          protegidas
        </p>
        <p data-id="element-266">
          Producto 100% digital en PDF · Sin envíos físicos · Sin impresión
          necesaria
        </p>
        <p className="mt-2" data-id="element-267">
          Este material tiene fines educativos y de desarrollo personal. No
          reemplaza la orientación de profesionales de la salud mental u otras
          áreas especializadas.
        </p>
        <p className="mt-2" data-id="element-268">
          Para consultas y más recursos visitá www.tufenixdigital.store
        </p>
      </footer>
    </main>;
}